import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useIndicator, usePrefersReducedMotion, useTween } from '../theme/hooks';

/** Bento card. `i` staggers the rise; `area` places it in the dashboard grid; `stack` drops the actions under the title on narrow phones. */
export const Card: React.FC<React.HTMLAttributes<HTMLElement> & { i: number; area: string; title?: React.ReactNode; icon?: React.ReactNode; actions?: React.ReactNode; stack?: boolean }> = ({
  i,
  area,
  title,
  icon,
  actions,
  stack,
  className,
  children,
  style,
  ...rest
}) => (
  <section className={cn('oa-card', `oa-a-${area}`, className)} style={{ ['--i' as string]: i, ...style }} {...rest}>
    {title || actions ? (
      <div className={cn('oa-ch', stack && 'is-stack')}>
        {title ? (
          <h2>
            {icon}
            {title}
          </h2>
        ) : (
          <span />
        )}
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    ) : null}
    {children}
  </section>
);

/** Segmented control with a spring indicator (tabs role). */
export function Seg<T extends string>({ options, value, onChange, label, className }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; label: string; className?: string }) {
  const ind = useIndicator<HTMLDivElement>('[aria-selected="true"]', [value, options.length]);
  return (
    <div ref={ind.ref} className={cn('oa-seg', className)} role="tablist" aria-label={label}>
      <span className="oa-ind" style={ind.style} aria-hidden="true" />
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A number that counts up on first render and tweens on change. */
export const Tween: React.FC<{ value: number; format?: (n: number) => string; duration?: number; className?: string }> = ({ value, format = (n) => Math.round(n).toLocaleString('en-GB'), duration, className }) => {
  const reduce = usePrefersReducedMotion();
  const shown = useTween(value, duration, reduce);
  return <span className={className}>{format(shown)}</span>;
};

/** ↑ 12.4% / ↓ 3.0%; nothing when there is no previous figure. */
export const DeltaChip: React.FC<{ pct: number | null; lowerIsBetter?: boolean }> = ({ pct, lowerIsBetter }) => {
  if (pct === null || !Number.isFinite(pct)) return <span className="oa-chip is-plain">New</span>;
  const up = pct >= 0;
  const good = lowerIsBetter ? !up : up;
  return (
    <span className={cn('oa-chip', Math.abs(pct) < 0.05 ? 'is-plain' : good ? 'is-good' : 'is-bad')}>
      {up ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

export const Skel: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => <div className={cn('oa-skel', className)} style={style} aria-hidden="true" />;

export const CardError: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-1 items-center gap-2.5 rounded-[10px] border border-dashed border-[var(--oa-line-2)] px-4 py-6 text-[13px] text-[var(--oa-muted)]">
    <AlertTriangle className="h-4 w-4 shrink-0 text-[var(--oa-warn)]" />
    <span className="min-w-0">{message}</span>
  </div>
);
